/**
 * OKR Service
 * API calls for OKR management
 */

import { api } from './api';
import type {
  OKR,
  CreateOKRRequest,
  UpdateOKRRequest,
  AddKeyResultRequest,
  UpdateProgressRequest,
  OKRFilters,
  OKRListResponse,
  KeyResult,
  FilterMetadata
} from '../types/okr';

class OKRService {
  /**
   * Create a new OKR
   */
  async createOKR(data: CreateOKRRequest): Promise<OKR> {
    const response = await api.post('/okrs', data);
    return response.data.data.okr;
  }

  /**
   * Get all OKRs with optional filters
   */
  async getOKRs(filters?: OKRFilters): Promise<OKRListResponse> {
    // Serialize array params as comma-separated strings (backend expects this format)
    const params: Record<string, string | number | undefined> = {};
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert arrays to comma-separated strings
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params[key] = value.join(',');
            }
          } else {
            params[key] = value;
          }
        }
      });
    }
    const response = await api.get('/okrs', { params });
    return response.data.data;
  }

  /**
   * Get EVERY OKR matching the filters, walking all pages.
   *
   * `GET /okrs` defaults to 20 rows per page. A caller that needs the whole set — the
   * Alignment Map draws a parent→child line only when both OKRs are loaded — must not
   * stop at page 1, or older parents silently drop off and their lines vanish.
   */
  async getAllOKRs(filters?: Omit<OKRFilters, 'page' | 'limit'>): Promise<OKRListResponse> {
    const limit = 100; // backend validator maximum
    const first = await this.getOKRs({ ...filters, page: 1, limit });
    const okrs = [...first.okrs];
    for (let page = 2; page <= first.totalPages; page++) {
      const next = await this.getOKRs({ ...filters, page, limit });
      okrs.push(...next.okrs);
    }
    return { ...first, okrs, page: 1, limit: okrs.length, totalPages: 1 };
  }

  /**
   * Get OKR by ID
   */
  async getOKRById(id: string): Promise<OKR> {
    const response = await api.get(`/okrs/${id}`);
    return response.data.data.okr;
  }

  /**
   * Update OKR
   */
  async updateOKR(id: string, data: UpdateOKRRequest): Promise<OKR> {
    const response = await api.patch(`/okrs/${id}`, data);
    return response.data.data.okr;
  }

  /**
   * Close OKR (set status to closed)
   */
  async closeOKR(id: string): Promise<OKR> {
    const response = await api.post(`/okrs/${id}/close`);
    return response.data.data.okr;
  }

  /**
   * Delete OKR
   */
  async deleteOKR(id: string): Promise<void> {
    await api.delete(`/okrs/${id}`);
  }

  /**
   * Duplicate OKR
   * @param id - Source OKR ID to duplicate
   * @param newOwnerId - Optional: assign to different owner (managers/admins only)
   */
  async duplicateOKR(id: string, newOwnerId?: string): Promise<OKR> {
    const response = await api.post(`/okrs/${id}/duplicate`, { newOwnerId });
    return response.data.data.okr;
  }

  /**
   * Add key result to OKR
   */
  async addKeyResult(okrId: string, data: AddKeyResultRequest): Promise<KeyResult> {
    const response = await api.post(`/okrs/${okrId}/key-results`, data);
    return response.data.data.keyResult;
  }

  /**
   * Update key result progress
   */
  async updateProgress(keyResultId: string, data: UpdateProgressRequest): Promise<KeyResult> {
    const response = await api.post(`/okrs/key-results/${keyResultId}/progress`, data);
    return response.data.data.keyResult;
  }

  /**
   * Update key result details
   */
  async updateKeyResult(keyResultId: string, data: Partial<AddKeyResultRequest>): Promise<KeyResult> {
    const response = await api.patch(`/okrs/key-results/${keyResultId}`, data);
    return response.data.data.keyResult;
  }

  /**
   * Delete key result
   */
  async deleteKeyResult(keyResultId: string): Promise<void> {
    await api.delete(`/okrs/key-results/${keyResultId}`);
  }

  /**
   * Duplicate key result to a target OKR
   */
  async duplicateKeyResult(keyResultId: string, targetOkrId: string): Promise<KeyResult> {
    const response = await api.post(`/okrs/key-results/${keyResultId}/duplicate`, {
      targetOkrId
    });
    return response.data.data.keyResult;
  }

  /**
   * Get available parent OKRs for alignment
   */
  async getAvailableParentOKRs(
    okrType: 'company' | 'department' | 'team' | 'individual',
    excludeId?: string
  ): Promise<Array<{
    id: string;
    title: string;
    type: string;
    status: string;
    owner: {
      firstName: string;
      lastName: string;
    };
  }>> {
    const response = await api.get('/okrs/available-parents', {
      params: { type: okrType, excludeId }
    });
    return response.data.data.okrs;
  }

  /**
   * Get filter metadata for advanced filtering
   */
  async getFilterMetadata(): Promise<FilterMetadata> {
    const response = await api.get('/okrs/filter-metadata');
    return response.data.data;
  }

  /**
   * Get organization-level stats for dashboard
   */
  async getOrganizationStats(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    totalActiveOKRs: number;
    avgProgress: number;
    totalKeyResults: number;
    checkInsDue: number;
    closedOKRs: number;
  }> {
    const response = await api.get('/okrs/org-stats', { params });
    return response.data.data;
  }
}

export const okrService = new OKRService();
